import React, { useMemo } from 'react';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import { FileUploader } from 'v3/Components/Form/FileUploader';
import { CONTROLLERS } from 'Models/Templates/Routes/Routes';
import { RequestStatus, useIdParam, useLineOfBusinessParam } from 'Utilities';
import { TitleActive } from 'v3/Components/Form/TitleActive';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';
import { InputActive } from 'v3/Components/Form/InputActive';
import { FilePondFile } from 'filepond';
import './AddEditReferralsRequest.AdditionalInformation.scss';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { FieldError } from 'v3/Components/Form/FormSection';
import {
  createReferralRequestSchema,
  useReferralRequestForm,
} from './useReferralRequestForm';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { Render } from 'Components/Display';

type AdditionalInformationProps = {
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
  onUploadStatusChange?: (isUploading: boolean) => void;
};

export const AdditionalInformationActive: React.FC<
  AdditionalInformationProps
> = ({ form, fieldErrors, onUploadStatusChange }) => {
  const [id, isAdd, isEdit] = useIdParam();

  const { lob } = useLineOfBusinessParam();
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();
  const isSubmitted =
    form.getFieldProps('requestStatus.displayName').value ===
    RequestStatus.SUBMITTED;

  return (
    <Render
      condition={
        isWorkersComp ||
        isIME ||
        isDisability ||
        isHealthReview ||
        isPharmacy ||
        isImpairmentRatingReview
      }
    >
      <div className="">
        <TitleActive>Special Instructions</TitleActive>
        <InputActive
          id="specialInstructions"
          name="specialInstructions"
          label="Comments"
          value={form.getFieldProps('specialInstructions').value ?? ''}
          onChange={(e: any) => {
            form.setFieldValue('specialInstructions', e.target.value);
          }}
          style={{
            width: '100%',
          }}
          error={
            fieldErrors?.find((f) => f.fieldId === 'specialInstructions')?.error
          }
        />
        <TitleActive>Medical Records</TitleActive>
        <FileUploader
          label="Upload the necessary documents attached to this case."
          fileEndpoint={
            isSubmitted
              ? `${CONTROLLERS.ReferralsRequest}/Document`
              : `${CONTROLLERS.ReferralsRequestsDraft}/Document`
          }
          name={
            isSubmitted
              ? 'referralRequestDocuments'
              : 'referralRequestDraftDocuments'
          }
          isEdit={isEdit}
          onUploadStatusChange={onUploadStatusChange}
          allowMultiple
          formValues={form.values}
          onFilesChange={(files: any) =>
            onFilesChange(files, form, isSubmitted)
          }
          acceptedFileLabel=".pdf, .doc, .dox, .xls, .xlsx"
        ></FileUploader>
      </div>
    </Render>
  );
};
const onFilesChange = (
  files: FilePondFile[],
  form: ReturnType<typeof useReferralRequestForm>,
  isSubmitted: boolean
) => {
  const uploadedFiles = (files || []).filter((file) => !!file.serverId);

  const newFiles = uploadedFiles.map((file) => {
    return {
      fileId: file.serverId,
      fileName: file.filename,
      size: file.fileSize,
      id: file.getMetadata('backendId'),
    };
  });

  if (isSubmitted) {
    form.setFieldValue('referralRequestDocuments', newFiles);
  } else {
    form.setFieldValue('referralRequestDraftDocuments', newFiles);
  }
};

export const AdditionalInformationReadOnly: React.FC<
  AdditionalInformationProps
> = ({ form }) => {
  const lob = useLineOfBusinessParam().lob;
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );
  const isSubmitted =
    form.getFieldProps('requestStatus.displayName').value ===
    RequestStatus.SUBMITTED;
  const filesToRender = isSubmitted
    ? form.values.referralRequestDocuments
    : form.values.referralRequestDraftDocuments;

  return (
    <div className="read-only-content">
      <TitleReadOnly>Special Instructions</TitleReadOnly>
      <ReadOnlyField
        id="specialInstructions"
        schema={ReferralRequestSchema}
        value={form.getFieldProps('specialInstructions').value}
        className={'no-truncate'}
      />
      <TitleReadOnly>Document(s)</TitleReadOnly>
      {!filesToRender?.length ? (
        <p>No documents</p>
      ) : (
        filesToRender.map((c) => (
          <div key={c.fileId} className="document">
            <DescriptionOutlinedIcon />
            <span>{c.fileName}</span>
          </div>
        ))
      )}
    </div>
  );
};
